import React from "react";
import {connect} from "react-redux";
import {FormattedMessage, useIntl} from "react-intl";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";

import {postRegenerateLookAiImage} from "../../aiApi/aiRegenerateApi";
import {setLookAICustomPrompt, setLookAiLimitBoxOpened} from "../../aiActions";
import {isAiRenderLimitReached} from "../../aiConfig";
import {getSignal, newController} from "../../aiApi/aiController";
import {getCommonVisualizerType} from "../../../../config";
import COLOR_THEMES from "../../../../colorThemes";
import {useAppSelector} from "../../../../storeHooks";

// The custom prompt block revealed at the bottom of the AI image overlay when the user starts a
// free-text edit of the result on screen. It is not tied to multiImage results any more - the
// edit mode is entered either from the "edit" button of one of the pieces of a multiImage result
// (see LookAIMultiImage / AIResultCustomPromptButtons) or from the "edit" button rendered next to
// "download" for every other result (see LookAIImageOverlayImageContent). The block is 80px high
// and is absolutely positioned at the bottom of the overlay: it slides in from below OVER the
// image (white background, so it stays readable) - the image itself keeps its size.
// The textarea and the button are both kept at the height of LookAIPopupGenerateButton
// (.look-button / .ui-button2 + u-h-25 = 2.5rem). The close button in its top right corner
// leaves the edit mode (onClose) - the overlay's own close button closes the whole overlay.
//
// "Regenerate" does NOT go through the /start-convert + poll flow of the generate button: it
// posts the image being edited (the cut piece of a multiImage result, or the whole result image)
// plus the typed prompt to the dedicated edit-image endpoint and swaps the returned image back
// in - see postRegenerateLookAiImage in aiApi/aiRegenerateApi.

export const AI_EDIT_BLOCK_HEIGHT = 80; // px, keep in sync with .look-ai__overlay-edit-block

type Props = {
  opened: boolean,
  onClose: () => void,
  lookAICustomPrompt: string,
  aiLoading: boolean,
  aiRegenerateLoading: boolean,
  regenerateError: string,
  aiResults: any[],
  setLookAiLimitBoxOpened: (lookAiLimitBoxOpened: boolean) => void,
  setLookAICustomPrompt: (lookAICustomPrompt: string) => void,
  postRegenerateLookAiImage: (signal?: AbortSignal) => void,
};

const AIResultCustomPrompt = ({
  opened,
  onClose,
  lookAICustomPrompt,
  aiLoading,
  aiRegenerateLoading,
  regenerateError,
  aiResults,
  setLookAiLimitBoxOpened,
  setLookAICustomPrompt,
  postRegenerateLookAiImage,
}: Props) => {

  const {activeTheme, isMobile} = useAppSelector(state => state.serverState);
  const intl = useIntl();

  const disabled = aiLoading || aiRegenerateLoading
    || !lookAICustomPrompt || !lookAICustomPrompt.trim();

  const onRegenerateClick = () => {
    if (disabled) {
      return;
    }
    if (isAiRenderLimitReached(aiResults)) {
      setLookAiLimitBoxOpened(true);
      return;
    }
    const controller = newController(); // replace the shared controller
    postRegenerateLookAiImage(controller.signal ?? getSignal());
  };

  const visualizerType = getCommonVisualizerType();

  return (
    <div style={{height: AI_EDIT_BLOCK_HEIGHT}}
         className={"look-ai__overlay-edit-block u-p-05" + (opened ? " look-ai__overlay-edit-block--opened" : "")}>
      <IconButton aria-label={intl.formatMessage({id: "iconButton.close"})}
                  className="look-ai__overlay-edit-close z-10"
                  onClick={onClose}>
        <CloseIcon/>
      </IconButton>
      <div className="h-full flex !items-end u-gap-05">
        <div className="flex--1">
          <div className="">
            <InputLabel shrink
                        htmlFor="lookAICustomPrompt"
                        sx={{
                          color: COLOR_THEMES[activeTheme].primary,
                          transform: "none",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          lineHeight: isMobile ? 1 : 1.2,
                          marginBottom: "2px",
                          whiteSpace: "normal",
                        }}>
              {regenerateError
                ? <span className="look-ai__overlay-edit-error">{regenerateError}</span>
                : <FormattedMessage id={"look.ai.overlay.customPromptPlaceholder"} values={{visualizerType}}/>}
            </InputLabel>
            <TextField id="lookAICustomPrompt"
                       size="small"
                       variant="outlined"
                       fullWidth={true}
                       value={lookAICustomPrompt}
                       multiline
                       maxRows={2}
                       disabled={aiRegenerateLoading}
                       onChange={(event) => setLookAICustomPrompt(event.target.value)}
                       sx={{
                         "& .MuiOutlinedInput-notchedOutline": {
                           top: 0,
                         },
                         "& .MuiOutlinedInput-notchedOutline > legend": {
                           display: "none",
                         },
                         "& .MuiInputBase-root": {
                           height: "40px",
                         },
                         "& .MuiInputBase-root textarea": {
                           lineHeight: 1,
                         },
                         height: "40px",
                       }}
            />
          </div>
        </div>
        <div className="nowrap">
          <Button className={"nowrap look-ai__overlay-edit-button ui-button4 ui-button4--primary look-button look-button--primary u-rounded-6 u-pr-125 u-pl-125 !text-[12px]"}
                  disabled={disabled}
                  onClick={onRegenerateClick}>
            <FormattedMessage id="look.ai.overlay.regenerateButton"/>
          </Button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (store: any) => {
  const {lookAICustomPrompt, aiLoading, aiRegenerateLoading, lookAIEditPiece, aiResults} = store.aiState;

  return {
    lookAICustomPrompt,
    aiLoading,
    aiRegenerateLoading,
    aiResults,
    regenerateError: (lookAIEditPiece && lookAIEditPiece.regenerateError) || "",
  };
};

const mapDispatchToProps = {
  setLookAICustomPrompt,
  setLookAiLimitBoxOpened,
  postRegenerateLookAiImage,
};

export default connect(mapStateToProps, mapDispatchToProps)(AIResultCustomPrompt)
